/**
 * Lift Truck PM Inspection — бумажные бланки: LiftOne «IC / Electric Lift Truck Planned
 * Maintenance Inspection Report» (погрузчики), JLG «Pre-Delivery, Frequent, and Annual
 * Inspection Report» (телескопы и бум-лифты) и Skyjack «Pre-Delivery/Maintenance
 * Inspection Checklist» (ножничные подъёмники).
 *
 * Разделы и порядок — как на бумаге, текст пунктов дословно. Ключи неизменяемы и
 * совпадают с src/Service/LiftTruckPmChecklist.php: сервер принимает ответы только по
 * своим ключам, чужие молча отбросит. Пункт Safety «Operator Training» заказчик убрал
 * с обоих бланков LiftOne — ключ safety.operator_training под другой пункт не занимать.
 * Со Skyjack убрано зачёркнутое: двигатель, топливо, пропан, мачта, ось, Engine Tray;
 * с бум-лифта — SkyGuard, гусеницы, трейлер, оси, аутригеры и двигатель.
 */

/**
 * Какой бланк открыть — решает powerType единицы техники: сервер выводит его из
 * внешней Equiptype, в админке его можно поправить. Имя историческое: сначала
 * различали только IC и Electric.
 */
export const POWER_TYPE = { IC: 'ic', ELECTRIC: 'electric', TELEHANDLER: 'telehandler', SCISSOR: 'scissor', BOOM: 'boom' };

export const POWER_TYPE_LABELS = {
    [POWER_TYPE.IC]: 'IC',
    [POWER_TYPE.ELECTRIC]: 'Electric',
    [POWER_TYPE.TELEHANDLER]: 'Telehandler',
    [POWER_TYPE.SCISSOR]: 'Scissor Lift',
    [POWER_TYPE.BOOM]: 'Boom Lift',
};

/** Тип техники + «Planned Maintenance Inspection Report» — как на сервере (FORM_TITLES). */
export const FORM_TITLES = {
    [POWER_TYPE.IC]: 'Gas Forklift Planned Maintenance Inspection Report',
    [POWER_TYPE.ELECTRIC]: 'Electric Forklift Planned Maintenance Inspection Report',
    [POWER_TYPE.TELEHANDLER]: 'Telehandler Planned Maintenance Inspection Report',
    [POWER_TYPE.SCISSOR]: 'Scissor Lift Planned Maintenance Inspection Report',
    [POWER_TYPE.BOOM]: 'Boom Lift Planned Maintenance Inspection Report',
};

/** Бланк LiftOne (погрузчики): своя шапка, топливо/напряжение и ответы OK / ADJ. */
export const isLiftOneSheet = (powerType) => powerType === POWER_TYPE.IC || powerType === POWER_TYPE.ELECTRIC;

/** Last PM (Equip Docs) у ножничного и бум-лифта не показывается: у них программа Annual. */
export const hasLastPm = (powerType) => powerType !== POWER_TYPE.SCISSOR && powerType !== POWER_TYPE.BOOM;

/** На JLG и Skyjack общий блок называется Comments, на LiftOne — Remarks. */
export const usesComments = (powerType) =>
    [POWER_TYPE.TELEHANDLER, POWER_TYPE.SCISSOR, POWER_TYPE.BOOM].includes(powerType);

/** Фото к форме — столько же принимает сервер (LiftTruckPmInspection::MAX_PHOTOS). */
export const MAX_PHOTOS = 12;

/** Подпись к фото — столько же символов принимает сервер. */
export const MAX_CAPTION_LENGTH = 500;

/** Строка «GAS □ LP GAS □ DIESEL □» IC-бланка. */
export const FUEL_TYPES = [
    { key: 'gas', label: 'Gas' },
    { key: 'lp_gas', label: 'LP Gas' },
    { key: 'diesel', label: 'Diesel' },
];

/** Строка «VOLTAGE: □24V □36V □48V □OTHER: ____» Electric-бланка. */
export const VOLTAGE_OTHER = 'other';
export const VOLTAGES = [
    { key: '24v', label: '24V' },
    { key: '36v', label: '36V' },
    { key: '48v', label: '48V' },
    { key: VOLTAGE_OTHER, label: 'Other' },
];

/**
 * Ответ пункта. LiftOne — OK / ADJ (третья колонка бланка, NEED REPAIR, по указанию
 * заказчика стала комментарием к пункту с надиктовкой); JLG и Skyjack — OK / N/A / ADJ.
 * Текст комментария лежит в itemComments. Цвета кнопок — severity-цвета JSSI.
 */
export const RESULT = { OK: 'ok', NA: 'na', ADJ: 'adj' };

const RESULT_OK = { key: RESULT.OK, label: 'OK', color: 'success' };
const RESULT_NA = { key: RESULT.NA, label: 'N/A', color: 'info' };
const RESULT_ADJ = { key: RESULT.ADJ, label: 'ADJ', color: 'warning' };

/** Кнопки ответа бланка, в порядке бланка. */
export const getResults = (powerType) => (
    isLiftOneSheet(powerType) ? [RESULT_OK, RESULT_ADJ] : [RESULT_OK, RESULT_NA, RESULT_ADJ]
);

/** Ответ из известных — старые/чужие значения ответом не считаются. */
export const isAnswered = (value) => [RESULT.OK, RESULT.NA, RESULT.ADJ].includes(value);

/** Пункт отмечен: есть ответ OK / ADJ или написан комментарий. */
export const isItemDone = (value, comment) => isAnswered(value) || String(comment || '').trim() !== '';

/** Столько же символов комментария принимает сервер. */
export const MAX_COMMENT_LENGTH = 4000;

/** Пункты с полем «____%» на бланке — значение хранится отдельным полем формы. */
export const PERCENT_FIELDS = {
    'upright.chains_adjustment_wear': 'chainWearPercent',
    'upright.fork_pins_wear': 'forkWearPercent',
};

/** Столько же ремарок принимает сервер. */
export const MAX_REMARKS = 50;

const SAFETY_IC = [
    ['horn', 'Horn'],
    ['warning_devices', 'Warning Devices (Alarm(s), Strobes, etc.)'],
    ['overhead_guard', 'Overhead Guard'],
    ['mast_backrest_fork_chain', 'Mast, Load Backrest, Fork or Chain Damage/Excessive Wear'],
    ['lights', 'Lights'],
    ['tire_damage', 'Tire Damage/Excessive Wear'],
    ['brakes_service_parking', 'Brakes - Service or Parking'],
    ['operator_restraint', 'Operator Restraint System'],
    ['hood_latches', 'Hood Latches'],
    ['capacity_plate_decals', 'Capacity/Data Plate & Warning Decals'],
    ['operators_manual', "Operator's Manual"],
    ['lp_tank_brackets', 'LP Tank/Bracket(s)/Date Codes'],
    ['fire_extinguisher', 'Fire Extinguisher'],
];

const SAFETY_ELECTRIC = [
    ['horn', 'Horn'],
    ['warning_devices', 'Warning Devices (Alarm(s), Strobes, etc.)'],
    ['overhead_guard', 'Overhead Guard'],
    ['mast_backrest_fork_chain', 'Mast, Load Backrest, Fork or Chain Damage/Excessive Wear'],
    ['lights', 'Lights'],
    ['tire_damage', 'Tire Damage/Excessive Wear'],
    ['load_wheels_casters', 'Load Wheels/Casters/Base Arms'],
    ['brakes_service_parking', 'Brakes - Service or Parking'],
    ['operator_restraint', 'Operator Restraint System'],
    ['hood_latches', 'Hood Latches'],
    ['capacity_plate_decals', 'Capacity/Data Plate & Warning Decals'],
    ['operators_manual', "Operator's Manual"],
    ['fire_extinguisher', 'Fire Extinguisher'],
];

const OPERATIONAL_IC = [
    ['fluid_leaks', 'Fluid Leaks'],
    ['neutral_starting_switch', 'Neutral Starting Switch/Starting System'],
    ['instruments_gauges', 'Instruments/Gauges/Warning Lights'],
    ['service_brakes', 'Service Brakes - Firm Resistance'],
    ['parking_brake', 'Parking Brake - No Drive Through'],
    ['inching_pedal_clutch', 'Inching Pedal/Clutch - No Slipping'],
    ['directional_control_lever', 'Directional Control Lever'],
    ['hydraulic_control_levers', 'Hydraulic Control Lever(s)'],
    ['frame_sheet_metal', 'Frame & Sheet Metal Damage'],
    ['exhaust_emissions', 'Exhaust/Emissions'],
    ['steering_responsiveness', 'Steering Responsiveness'],
    ['unusual_noise', 'Unusual Noise'],
    ['mast_carriage_operations', 'Mast & Carriage Operations'],
    ['attachment_operations', 'Attachment Operations'],
];

const OPERATIONAL_ELECTRIC = [
    ['fluid_leaks', 'Fluid Leaks'],
    ['seat_foot_switch', 'Seat/Foot Switch'],
    ['instruments_gauges_bdi', 'Instruments/Gauges/Warning Lights/Battery Discharge Indicator'],
    ['service_brakes', 'Service Brakes - Firm Resistance'],
    ['parking_brake', 'Parking Brake - No Drive Through'],
    ['directional_control_lever_sro', 'Directional Control Lever - SRO + Plugging'],
    ['hydraulic_control_levers', 'Hydraulic Control Lever(s)'],
    ['frame_sheet_metal', 'Frame & Sheet Metal Damage'],
    ['steering_responsiveness', 'Steering Responsiveness'],
    ['unusual_noise', 'Unusual Noise'],
    ['mast_carriage_operations', 'Mast & Carriage Operations'],
    ['attachment_operations', 'Attachment Operations'],
];

const ENGINE_IC = [
    ['engine_running_condition', 'Engine Running Condition'],
    ['battery_cables_harnesses', 'Battery Cables, Connections & Wire Harnesses'],
    ['engine_coolant_level', 'Check Engine Coolant Level'],
    ['radiator_cap_hoses', 'Radiator, Cap, Hoses & Connections'],
    ['belts_fan_blade', 'Belts & Fan Blade connections'],
    ['shroud_fan_drive', 'Shroud & Fan Drive'],
    ['transmission_oil_lines', 'Transmission Oil & Lines'],
    ['hydraulic_oil_level_filter', 'Hydraulic Oil Level & Filler Cap/Hydraulic Filter'],
    ['hydraulic_system_control_valve', 'Hydraulic System & Control Valve/Hydraulic Lines'],
    ['master_cylinder_reservoir', 'Check Master Cylinder & Reservoir'],
    ['governor_rpm', 'Governor RPM'],
];

const ELECTRIC_VEHICLE = [
    ['hydraulic_oil_level_filter', 'Hydraulic Oil Level & Filler Cap/Hydraulic Filter'],
    ['battery_electrolyte_watering', 'Battery Electrolyte Levels & Watering Procedure'],
    ['battery_cables_connections', 'Battery Cable(s) & Connection(s)'],
    ['power_cables_harnesses', 'Power Cables & Wire Harnesses'],
    ['master_cylinder_reservoir', 'Check Master Cylinder & Reservoir'],
    ['hydraulic_lines_control_valve', 'Hydraulic Lines & Connections/Control Valve'],
    ['contactor_panel_tip_wear', 'Contractor Panel Condition/Tip Wear'],
];

const LINKAGE_IC = [
    ['park_brake_lever_cables', 'Park Brake Lever & Cables'],
    ['clutch_inching_brake_pedal', 'Clutch Inching Pedal/Brake Pedal'],
    ['accelerator_directional_lever', 'Accelerator Pedal/Directional Lever'],
    ['monotrol_pedal_levers', 'Monotrol® Pedal, Lever(s)'],
];

const LINKAGE_ELECTRIC = [
    ['park_brake_lever_cables', 'Park Brake Lever & Cables'],
    ['brake_pedal_linkage', 'Brake Pedal Linkage'],
    ['monotrol_pedal_levers_accelerator', 'Monotrol® Pedal/Lever(s)/Accelerator Controller'],
];

const MOTOR_BRUSH_WEAR = [
    ['drive_motor', 'Drive Motor'],
    ['hydraulic_motor', 'Hydraulic Motor'],
    ['auxiliary_steering_motor', 'Auxiliary/Steering Motor'],
];

const STEER_IC = [
    ['steer_axle_mounting', 'Check Steer Axle Mounting Hardware & Lube Fittings'],
    ['king_pins_bushings', 'Check King Pins & Bushings, Tire Rods, Spindles'],
    ['steer_cylinder_lines', 'Check Steer Cylinder & Lines'],
    ['steer_wheels_mounting', 'Check Steer Wheels & Mounting Hardware'],
];

const STEER_ELECTRIC = [
    ['steer_axle_mounting', 'Check Steer Axle Mounting Hardware & Lube Fittings'],
    ['king_pins_bushings', 'Check King Pins & Bushings, Tire Rods, Spindles'],
    ['steer_wheels_mounting', 'Check Steer Wheels & Mounting Hardware'],
    ['steer_chain', 'Check Steer Chain'],
    ['steer_cylinder_lines', 'Check Steer Cylinder & Lines'],
];

const DRIVE_AXLE = [
    ['drive_wheels_mounting', 'Drive Wheels & Mounting Hardware'],
    ['mdu_differential_oil', 'Check MDU/Differential Oil'],
];

const UPRIGHT_IC = [
    ['hangar_caps', 'Check Hangar Caps, Bushings & Hardware & Lube Fittings'],
    ['upright_rollers', 'Upright Rollers, Bearing Strips'],
    ['chains_adjustment_wear', 'Chains, Chain Anchors, Chain Adjustment/Wear'],
    ['carriage_rollers', 'Carriage Rollers & Mounting Hardware'],
    ['lift_cylinders', 'Lift Cylinders'],
    ['tilt_cylinders', 'Tilt Cylinders, Lines & Connections'],
    ['fork_pins_wear', 'Fork Pins & Locks/Fork Wear'],
    ['attachment_mounting', 'Attachment Mounting Hardware & Connections'],
];

const UPRIGHT_ELECTRIC = [
    ['hangar_caps', 'Hangar Caps, Bushings & Hardware & Lube Fittings'],
    ['upright_rollers', 'Upright Rollers, Bearing Strips'],
    ['chains_adjustment_wear', 'Chains, Chain Anchors, Chain Adjustment/Wear'],
    ['carriage_rollers', 'Carriage Rollers & Mounting Hardware'],
    ['lift_cylinders', 'Lift Cylinders'],
    ['tilt_cylinders', 'Tilt Cylinders, Lines & Connections'],
    ['fork_pins_wear', 'Fork Pins & Locks/Fork Wear'],
    ['attachment_mounting', 'Attachment Mounting Hardware & Connections'],
];

const ANNUAL_IC = [
    ['powershift_oil_filter', 'Change Powershift Oil & Filter'],
    ['differential_speed_reducer_final_drive', 'Change Differential Oil, Speed Reducer & Final Drive Lubricants'],
    ['hydraulic_oil_filter', 'Change Hydraulic Oil & Filter'],
    ['brake_fluid', 'Change Brake Fluid'],
    ['pull_wheels_brakes_seals', 'Pull Wheels To Inspect Brake System/Check Wheel Seals'],
    ['repack_wheel_bearings', 'Repack Wheel Bearings'],
    ['engine_valves_retime', 'Adjust Engine Valves & Retime'],
    ['fuel_filter', 'Replace Fuel Filter'],
    ['pcv_valve', 'Change PVC Valve'],
    ['coolant', 'Drain, Flush & Change Coolant'],
];

const ANNUAL_ELECTRIC = [
    ['hydraulic_oil_filter', 'Change Hydraulic Oil & Filter'],
    ['brake_fluid', 'Change Brake Fluid'],
    ['pull_wheels_brakes_seals', 'Pull Wheels To Inspect Brake System/Check Wheel Seals'],
    ['repack_wheel_bearings', 'Repack Wheel Bearings'],
    ['differential_oil', 'Change Differential Oil'],
];

const FUEL_SYSTEM_IC = [
    ['tank_lines_cap', 'Tank, Lines, Fuel Cap'],
    ['lp_fittings_hose', 'LP Fittings & Hose'],
    ['filter_lockoff_carburetor', 'Filter Lock-off/Carburetor'],
    ['fuel_filter_bowl', 'Fuel Filter & Filter Bowl'],
    ['air_filter_hoses', 'Air Filter/Connections & Hoses'],
    ['choke_stop_cable', 'Choke/Stop Cable'],
];

const ANNUAL_TITLE = 'Annual or 2000 Hour Service';
const ANNUAL_SUBTITLE = 'Recommended services at additional charge';

// ─── JLG TF6-42 Material Handler — телескопы ────────────────────────────────

const TH_FUNCTIONS = [
    ['all_controls_neutral', 'All controls return to neutral/off position when released.'],
    ['detents_lock_controls', 'Detents properly lock controls in neutral/off position. Check condition of control enclosures & protective boots/guards. (Platform Controls)'],
    ['footswitch', 'Footswitch operates properly. (shuts off functions when released)'],
    ['emergency_stop', 'Emergency stop switches at the cab & platform control stations arrest all platform movement.'],
    ['cut_outs', 'All function & speed cut-outs operate properly.'],
    ['auxiliary_power', 'Auxiliary power system operates properly.'],
    ['capacity_indicator', 'Capacity indicator system operates properly.'],
    ['service_parking_brakes', 'Service and Parking Brakes operate properly.'],
    ['machine_controls', 'Machine controls operate properly at platform & cab control station (lift, drive, telescope, etc.).'],
    ['travel_backup_alarm', 'Travel/Backup alarm function properly from the platform and cab controls.'],
    ['instruments_horn_lights', 'Instruments, hour meter, switches, gauges, horn & lights operate properly.'],
];

const TH_PLATFORM = [
    ['platform_installed', 'Platform installed & secure. Mounting hardware free of damage & secure. Gate(s) opens & latches properly.'],
    ['swing_down_saddles', 'Swing down saddles function properly.'],
    ['guardrails_floor', 'Platform guardrails and floor in place, secure and undamaged.'],
    ['lanyard_anchorage', 'Lanyard anchorage points secure, undamaged & labeled.'],
];

const TH_BOOM = [
    ['boom_sections', 'Boom sections properly shimmed, free of damage, distortion, & excessive wear.'],
    ['boom_chains_sheaves', 'Inspect boom chains, sheaves, sheave pins & bearings for proper adjustment, no excessive wear.'],
    ['pins_hardware', 'Inspect all pins and attaching hardware for proper installation, no excessive wear, secure & undamaged.'],
    ['quick_switch', 'Quick switch linkage is free, functions properly and attachments fit and secured properly.'],
];

const TH_CHASSIS = [
    ['lug_nuts', 'Lug nuts properly torqued.'],
    ['tires', 'Tires free of gouges and excessive wear, no cords showing. If pneumatic, properly inflated. Tire bead properly seated around rim.'],
    ['rear_axle_locking', 'Rear Axle Locking System operates properly, locks in place when boom is elevated above horizontal.'],
    ['seat_belt', 'Seat belt in good condition and latches properly.'],
    ['guards_covers', 'All guards and covers installed properly & in good working order.'],
    ['rops_fops', 'ROPS/FOPS Cab Structure. (No structural damage)'],
    ['mirror', 'Mirror clean and properly adjusted.'],
    ['fluid_levels', 'Fluid levels correct (hydraulic oil, fuel, coolant, engine oil, transmission, differential, and planetaries)'],
];

const TH_POWER = [
    ['engine_idle_rpm', 'Engine idle, throttle & RPM set properly.'],
    ['battery_fluid', 'Battery fluid levels correct.'],
    ['air_fuel_filter', 'Air & Fuel filter clean and secure.'],
    ['fuel_cap', 'Fuel cap tight & vent open.'],
    ['exhaust', 'Exhaust system free of leaks.'],
    ['radiator_oil_cooler', 'Radiator & oil cooler clean.'],
    ['transmission', 'Transmission functions properly.'],
];

const TH_HYDRAULIC = [
    ['cylinders', 'All cylinders free of damage, no evidence of leaks.'],
    ['hydraulic_components_areas', 'All areas around hydraulic components (pump, oil lines, reservoir, etc.) free of oil, no evidence of leaks.'],
    ['filter_breather', 'Hydraulic filter and reservoir breather clean.'],
    ['oil_level', 'Hydraulic oil level in reservoir correct.'],
    ['reservoir_cap', 'Hydraulic reservoir cap tight & vent open.'],
    ['fittings_lines', 'All hydraulic fittings & lines secure, free of damage, chafing & leaks.'],
    ['electrical_connections', 'All electrical connections tight, no corrosion or abrasions.'],
    ['auxiliary_pump_motor', 'Auxiliary pump & motor secure, undamaged & free of leaks.'],
];

const TH_MANUALS = [
    ['platform_manuals', 'ANSI/SIA Manual of Responsibilities, Operators & Safety Manual, and AEM Handbook in manual storage box on platform.'],
    ['cab_manuals', 'Operators & Safety Manual and Gradall Safety Manual in manual storage box in cab.'],
    ['capacity_decals', 'Capacity decals/charts installed & legible at both platform & cab stations.'],
    ['safety_decals', 'All safety & instructional decals installed, secure & legible.'],
];

const TH_GENERAL = [
    ['no_unauthorized_modifications', 'Lift is free of unauthorized modifications or additions.'],
    ['structural_welds', 'Inspect general structural condition including all welds.'],
    ['grease_lubricate', 'Grease and lubricate per Service & Maintenance Manual.'],
    ['safety_bulletins', 'Applicable Safety Bulletins completed.'],
];

const TH_ANNUAL = [
    ['steering_wheel_stops', 'Steering and wheel stops correct.'],
    ['record_inspection_date', 'Record inspection date on "IMPORTANT" decal on frame.'],
    ['boom_chains', 'Boom chains free of damage, properly lubricated, no excessive wear, cracks or distortion.'],
    ['nuts_bolts_pins', 'All nuts, bolts, pins, shafts, shields, bearings, wear pads, locking devices, checked for proper installation, no excessive wear, cracks, or distortion.'],
    ['hydraulic_pressures', 'All hydraulic pressures properly adjusted.'],
    ['ownership_changed', 'If ownership has changed, complete attached Owner Update form and return to JLG.'],
];

// ─── Skyjack Pre-Delivery/Maintenance — ножничные подъёмники ────────────────
// На бумаге один список без разделов (в две колонки — это только PDF).

const SCISSOR_ITEMS = [
    ['service_bulletins', 'Refer to skyjack.com for the latest service bulletins.'],
    ['annual_inspection_13_months', 'Ensure Annual Inspection has been completed within the last 13 months.'],
    ['manuals_documents', 'Manuals & Required Documents. In storage box, in good condition & legible.'],
    ['labels', 'Labels. In place, secure & legible.'],
    ['limit_switches', 'Limit Switches. Secured & no obstructions or damage.'],
    ['main_power_disconnect', 'Main Power Disconnect Switch. Cables secure & in working order.'],
    ['battery_hydraulic_tray', 'Battery/Hydraulic Tray. Latch is secure, & no missing parts.'],
    ['battery_charger', 'Battery Charger. Secure, & no damage.'],
    ['battery', 'Battery. No damage, tight connections, fluid levels correct. Clean terminals and cable ends.'],
    ['manifolds', 'Manifolds. Tight fittings and hoses & no damage or leaks. Tight wire connections & no missing parts.'],
    ['motor_controller', 'Motor Controller. Secure & no damage. No loose connections.'],
    ['electrical_panel', 'Electrical Panel / Control Module. Secure & no damage. Tight wire connections and fasteners.'],
    ['hydraulic_tank', 'Hydraulic Tank. Filler cap secure & no damage or leaks.'],
    ['hydraulic_oil', 'Hydraulic Oil. Level at, or slightly above top mark.'],
    ['hydraulic_components_hoses', 'Hydraulic Components & Hoses. Secure & no damage or leaks.'],
    ['base_weldment', 'Base Weldment. No deformation or cracks.'],
    ['base_control_switches', 'Base Control Switches. Switches to neutral position & no damage.'],
    ['free_wheeling_valve', 'Free-wheeling Valve Knob. Secure & no damage or missing parts.'],
    ['ladder', 'Ladder. Secure & no damage.'],
    ['ac_power_platform', 'AC Power to Platform (Plug Cord Receptacle). No obstructions, dirt or damage.'],
    ['pothole_protection', 'Pothole Protection Device. Check both sides for obstructions, dirt, or damage.'],
    ['brakes', 'Brakes. Secure & no damage or leaks.'],
    ['steer_cylinder', 'Steer Cylinder Assembly. Secure & no damage, leaks or missing parts.'],
    ['wheel_tire', 'Wheel/Tire Assembly. Check all tires for damage, wear & proper alignment. Lug nuts torqued as recommended.'],
    ['tie_rod', 'Tie Rod. End studs locked & no damage.'],
    ['tilt_sensor', 'Tilt (Load) Sensor. Secure & no damage.'],
    ['emergency_lowering_rod', 'Emergency Lowering Access Rod. Secure & no damage.'],
    ['scissor_assembly', 'Scissor Assembly. No deformation/damage. Pins secure. Cables & wires routed with no damage.'],
    ['rollers', 'Rollers. Secure & no obstructions, dirt, or damage/wear.'],
    ['wear_pads', 'Wear Pads. No damage/wear or missing parts. Fasteners tightened.'],
    ['scissor_bumpers', 'Scissor Bumpers. Secure & no damage.'],
    ['sliders', 'Sliders. Secure & no obstructions, dirt, or damage/wear.'],
    ['maintenance_support', 'Maintenance Support. Secure & no damage.'],
    ['lift_cylinders', 'Lift Cylinder(s). No damage or missing parts. Tight fittings and hoses & no leaks.'],
    ['scissor_pins', 'Scissor Pins. No damage/wear or missing parts.'],
    ['platform_control_console', 'Platform Control Console. Switches to neutral position & secure. No missing parts.'],
    ['railings_gate', 'Railings and Gate/Chain. Secure & no damage or missing parts.'],
    ['lanyard_anchorage', 'Lanyard Attachment Anchorage. Attachment rings secure & no damage.'],
    ['ac_outlet', 'AC Outlet. No obstructions, dirt, or damage.'],
    ['extension_control_console', 'Powered Extension Control Console. Switches to neutral position & secure. No missing parts.'],
    ['extension_deck', 'Extension Deck. Secure & no damage or missing parts. Check fluid level (if applicable).'],
    ['outriggers', 'Outriggers. No damage or missing parts.'],
    ['scissor_guards', 'Scissor Guards. Secure & no damage.'],
    ['greasing_points', 'Greasing Points. No obstructions, dirt, or damage.'],
    ['function_tests', "Function Tests (Refer to your corresponding Serial #'s Operating Manual for information on running these tests.)"],
];

// ─── JLG Boom Lift — бум-лифты ──────────────────────────────────────────────
// Ключи разделов свои (bl_…): текст пунктов не тот, что у телескопа JLG.

const BL_FUNCTIONS = [
    ['latest_software', 'Machine has the latest control system software.'],
    ['system_test', 'Perform System Test using JLG handheld analyzer.'],
    ['controls_neutral', 'All joystick/toggle controls return to neutral/off position when released.'],
    ['detents_lock_controls', 'Detents properly lock controls in neutral/off position. Check condition of control enclosures and protective boots/guards.'],
    ['footswitch', 'Footswitch operates properly, not modified, disabled or blocked.'],
    ['emergency_stop', 'All machine functions are disabled when Emergency Stop Button is activated at ground and/or platform control stations.'],
    ['limits_cut_outs', 'All function and speed limits/cut-outs operate properly.'],
    ['manual_descent', 'Manual descent system and/or auxiliary power system operates properly.'],
    ['tower_boom_sync', 'Tower boom synchronization and sequence system operates properly.'],
    ['load_sensing', 'Load Sensing System operates properly.'],
    ['capacity_indicator', 'Capacity indicator system operates properly.'],
    ['swing_drive_brakes', 'Swing and Drive Brakes operate properly.'],
    ['other_controls', 'All other machine controls, alarms, horn, displays, indicators, and functions operate properly at platform and ground control stations.'],
];

const BL_PLATFORM = [
    ['platform_installed', 'Platform properly installed and secure. Mounting hardware free of damage and secure. Gate opens and latches properly.'],
    ['guardrails_floor', 'Platform guardrails and floor in place, secure and free of damage.'],
    ['lanyard_anchorage', 'Lanyard anchorage points secure, free of damage and labeled.'],
];

const BL_BOOM = [
    ['boom_sections', 'Boom sections and related weldments free of damage, distortion and excessive wear.'],
    ['hardware_wear', 'Inspect all nuts, bolts, pins, shafts, shields, bearings, wear pads, locking devices, sheaves, sheave pins and bearings for proper installation, secure, no excessive wear, cracks or distortion.'],
    ['rotators', 'Inspect platform rotators and jib rotators for proper installation, free of damage, proper operation, and free of leaks.'],
    ['chains_cables', 'Boom chains and cables are free of damage, properly installed, properly torqued and properly lubricated.'],
    ['powertrack', 'Powertrack is free of damage, distortion and excessive wear. Cables/hoses are properly routed, no chafing or leaks.'],
    ['cylinder_pins', 'Inspect all cylinder pins, pivot pins, attaching and retention hardware for damage, excessive wear.'],
];

const BL_TURNTABLE = [
    ['doors_covers', 'Doors/covers fasten properly and are secure. Can be opened as intended.'],
    ['bearing_swing_drive', 'Turntable bearing, swing drive and gear secure, free of damage and properly lubricated. No missing bolts or signs of looseness.'],
    ['bearing_bolts_check', 'Perform turntable bearing mounting bolt condition check and wear tolerance check.'],
    ['turntable_lock', 'Turntable lock secure, free of damage and operates properly.'],
];

const BL_CHASSIS = [
    ['wheel_hardware', 'Wheel attachment hardware torqued properly. No missing attachment hardware.'],
    ['wheels_tires', 'Proper wheels/tires installed. Inspect for excessive damage, wear and corrosion. If pneumatic, properly inflated and mounted.'],
    ['steering_components', 'Steering components are properly installed, secure, and free of damage.'],
    ['drive_hubs', 'Check drive hubs for proper fluid level.'],
];

const BL_POWER = [
    ['battery_charger', 'Proper battery fluid level. Battery charger operates properly. Batteries accept charge.'],
];

const BL_HYDRAULIC = [
    ['cylinders', 'All cylinders free of damage with no evidence of leaks.'],
    ['no_leaks_areas', 'All areas around hydraulic components have no evidence of leaks.'],
    ['oil_serviced', 'Hydraulic oil system properly serviced and free of contaminants.'],
    ['hoses_fittings_lines', 'All hydraulic hoses, fittings and lines secure and free of damage. Check for chafing and leaks.'],
    ['electrical_connections', 'All electrical connections tight, no corrosion or abrasions.'],
    ['pump_motor', 'Pump and motor secure, free of damage and leaks.'],
    ['hydraulic_pressures', 'All hydraulic pressures properly adjusted.'],
];

const BL_MANUALS = [
    ['manuals', 'Operation and Safety Manual, ANSI/SAIA Manual of Responsibilities (ANSI markets only), and AEM Handbook (ANSI markets only) in manual storage box.'],
    ['decals', 'All safety, capacity and instructional decals installed, secure and legible.'],
];

const BL_GENERAL = [
    ['no_modifications', 'Machine is free of unauthorized modifications or additions.'],
    ['safety_bulletins', 'Applicable Safety Bulletins completed.'],
    ['structural_welds', 'Inspect general structural condition including all welds.'],
    ['grease_lubricate', 'Grease and lubricate per Service and Maintenance Manual.'],
    ['drive_operate_test', 'Drive and operate machine to test all machine functions.'],
    ['record_inspection_date', 'Record inspection date at "IMPORTANT" decal.'],
    ['notify_ownership', 'Notify JLG of any ownership changes.'],
    ['annual_due', 'Is Annual Inspection due?'],
];

const LAYOUTS = {
    [POWER_TYPE.ELECTRIC]: [
        ['safety', 'Safety', null, 'left', SAFETY_ELECTRIC],
        ['operational', 'Operational Checks', null, 'left', OPERATIONAL_ELECTRIC],
        ['electric_vehicle', 'Electric Vehicle Checks', null, 'left', ELECTRIC_VEHICLE],
        ['linkage', 'Check & Lube Linkage', null, 'left', LINKAGE_ELECTRIC],
        ['motor_brush', 'Electric Motor Brush Wear', null, 'right', MOTOR_BRUSH_WEAR],
        ['steer', 'Steer Axle & Steer System', null, 'right', STEER_ELECTRIC],
        ['drive_axle', 'Drive Axle Checks', null, 'right', DRIVE_AXLE],
        ['upright', 'Upright/Front End Checks', null, 'right', UPRIGHT_ELECTRIC],
        ['annual', ANNUAL_TITLE, ANNUAL_SUBTITLE, 'right', ANNUAL_ELECTRIC],
    ],
    [POWER_TYPE.IC]: [
        ['safety', 'Safety', null, 'left', SAFETY_IC],
        ['operational', 'Operational Checks', null, 'left', OPERATIONAL_IC],
        ['engine', 'Engine Compartment Checks & Lube', null, 'left', ENGINE_IC],
        ['steer', 'Steer Axle & Steer System', null, 'right', STEER_IC],
        ['drive_axle', 'Drive Axle Checks', null, 'right', DRIVE_AXLE],
        ['upright', 'Upright/Front End Checks', null, 'right', UPRIGHT_IC],
        ['annual', ANNUAL_TITLE, ANNUAL_SUBTITLE, 'right', ANNUAL_IC],
        ['fuel_system', 'Fuel System Checks', null, 'right', FUEL_SYSTEM_IC],
        ['linkage', 'Check & Lube Linkage', null, 'right', LINKAGE_IC],
    ],
    // Ключи разделов не пересекаются с LiftOne — ответы одного бланка не читаются как другого.
    [POWER_TYPE.TELEHANDLER]: [
        ['functions', 'Functions & Controls', null, 'left', TH_FUNCTIONS],
        ['platform', 'Platform Assembly', null, 'left', TH_PLATFORM],
        ['boom', 'Boom Assembly', null, 'left', TH_BOOM],
        ['chassis', 'Chassis', null, 'middle', TH_CHASSIS],
        ['power_system', 'Power System', null, 'middle', TH_POWER],
        ['hydraulic_electrical', 'Hydraulic/Electrical System', null, 'middle', TH_HYDRAULIC],
        ['manuals_decals', 'Manuals & Decals', null, 'right', TH_MANUALS],
        ['general', 'General', null, 'right', TH_GENERAL],
        ['annual_inspection', 'Annual Inspection', 'To be performed in addition to previous items', 'right', TH_ANNUAL],
    ],
    [POWER_TYPE.SCISSOR]: [
        ['items', 'Items for Inspection', null, 'left', SCISSOR_ITEMS],
    ],
    [POWER_TYPE.BOOM]: [
        ['bl_functions', 'Functions & Controls', null, 'left', BL_FUNCTIONS],
        ['bl_platform', 'Platform Assembly', null, 'left', BL_PLATFORM],
        ['bl_boom', 'Boom Assembly', null, 'left', BL_BOOM],
        ['bl_turntable', 'Turntable', null, 'middle', BL_TURNTABLE],
        ['bl_chassis', 'Chassis', null, 'middle', BL_CHASSIS],
        ['bl_power', 'Power System', null, 'middle', BL_POWER],
        ['bl_hydraulic', 'Hydraulic/Electrical System', null, 'right', BL_HYDRAULIC],
        ['bl_manuals', 'Manuals & Decals', null, 'right', BL_MANUALS],
        ['bl_general', 'General', null, 'right', BL_GENERAL],
    ],
};

/**
 * Разделы бланка: { key, title, subtitle, column: 'left'|'middle'|'right', items: [{ key, label, percentField }] }.
 * Для неизвестного типа — пустой список.
 */
export const getSections = (powerType) =>
    (LAYOUTS[powerType] || []).map(([sectionKey, title, subtitle, column, items]) => ({
        key: sectionKey,
        title,
        subtitle,
        column,
        items: items.map(([itemKey, label]) => {
            const key = `${sectionKey}.${itemKey}`;
            return { key, label, percentField: PERCENT_FIELDS[key] || null };
        }),
    }));

export const getItemKeys = (powerType) =>
    getSections(powerType).flatMap((section) => section.items.map((item) => item.key));

/** Техника, у которой есть бланк (тип выведен из Equiptype или выставлен в админке). */
export const hasChecklist = (equipment) =>
    !!equipment && equipment.status !== false && !!LAYOUTS[equipment.powerType];

/** Имя залогиненного сотрудника — по умолчанию в поле Tech, правится руками. */
export const defaultTechName = () => {
    try {
        return localStorage.getItem('employeeFullName') || '';
    } catch (e) {
        return '';
    }
};

/** Число или null: «1,234.5» и «1234.5» — одно и то же. */
export const parseNumber = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).replace(/[,\s%]/g, '').trim();
    if (normalized === '') return null;
    const number = Number(normalized);
    return Number.isFinite(number) ? number : null;
};
